import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit3, RefreshCw, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { WarehouseTable, useInlineEdit, createColumnFactory } from '../../../../shared/table';
import type { ColumnDefinition, TableAction, ServerFetchParams } from '../../../../shared/table/types/table';
import { itemBinSizeService } from '../../../../../services/itemBinSizeService';
import { ItemBinSizeDto, CreateItemBinSizeRequest, ItemBinSizePagedQueryParams } from '../../../../../types/itemBinSize';
import { stcBinSizeService } from '../../../../../services/stcBinSizeService';
import { itemBinSizeGroupService } from '../../../../../services/itemBinSizeGroupService';
import { StcBinSizeDto } from '../../../../../types/stcBinSize';
import { ItemBinSizeGroupDto } from '../../../../../types/itemBinSizeGroup';
import { useAuth } from '../../../../../contexts/AuthContext';

interface SelectedRows {
    [key: string]: boolean;
}

interface ItemBinSizeDataTableProps {
    selectedRows: SelectedRows;
    onRowSelection: (rowId: string, selected: boolean) => void;
    onBulkSelection: (rows: SelectedRows) => void;
    onDataChange: (data: ItemBinSizeDto[]) => void;
    onOpenSidePanel: () => void;
    refreshTrigger?: number;
}

const CreateForm: React.FC<{
    onClose: () => void;
    onSave: (request: CreateItemBinSizeRequest) => void;
    groups: ItemBinSizeGroupDto[];
    companyNo: string;
}> = ({ onClose, onSave, groups, companyNo }) => {
    const [mode, setMode] = useState<'direct' | 'group'>('direct');
    const [showInfo, setShowInfo] = useState(false);
    const [formData, setFormData] = useState<CreateItemBinSizeRequest>({
        companyNo: companyNo,
        itemNo: '',
        binSizeNo: 1,
        maxQuantity: 0,
        putAwayPriorityNo: 100
    });
    const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
    const [loadingLookups, setLoadingLookups] = useState<boolean>(false);

    useEffect(() => {
        const loadLookupData = async () => {
            try {
                setLoadingLookups(true);
                const binSizesData = await stcBinSizeService.getCurrent();
                setBinSizes(binSizesData);
            } catch (err: any) {
                console.error('Feil ved lasting av oppslagsdata:', err);
            } finally {
                setLoadingLookups(false);
            }
        };
        loadLookupData();
    }, []);

    const handleModeChange = (newMode: 'direct' | 'group') => {
        setMode(newMode);
        if (newMode === 'group') {
            setFormData(prev => ({ ...prev, itemNo: undefined, itemBinSizeGroupID: groups[0]?.itemBinSizeGroupID }));
        } else {
            setFormData(prev => ({ ...prev, itemNo: '', itemBinSizeGroupID: undefined }));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Opprett ny vare-lokasjonsstørrelse</h4>

            {/* Uniqueness info toggle-bar */}
            <button
                type="button"
                onClick={() => setShowInfo(prev => !prev)}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 mb-2"
            >
                <Info className="w-3.5 h-3.5" />
                <span>Krav til unikhet</span>
                {showInfo ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 text-xs text-blue-800 space-y-1">
                    <p className="font-medium">Kombinasjonen av disse feltene må være unik:</p>
                    <ul className="list-disc ml-4 space-y-0.5">
                        <li><strong>Direkte:</strong> Selskap + Varenr. + Lokasjonsstørrelse (kun én rad per vare per størrelse)</li>
                        <li><strong>Gruppemal:</strong> Selskap + Størrelsesgruppe + Lokasjonsstørrelse (kun én mal per gruppe per størrelse)</li>
                    </ul>
                    <p className="text-blue-600 mt-1">Ved konflikt vil opprettelsen feile med en feilmelding.</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
                {/* Mode selector */}
                <div className="flex items-center gap-4 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="createMode"
                            checked={mode === 'direct'}
                            onChange={() => handleModeChange('direct')}
                            className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">Direkte (per vare)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="createMode"
                            checked={mode === 'group'}
                            onChange={() => handleModeChange('group')}
                            className="w-4 h-4 text-blue-600"
                            disabled={groups.length === 0}
                        />
                        <span className={`text-sm ${groups.length === 0 ? 'text-gray-400' : 'text-gray-700'}`}>
                            Gruppemal (per størrelsesgruppe)
                        </span>
                    </label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Selskap</label>
                        <input
                            type="text"
                            value={formData.companyNo}
                            onChange={(e) => setFormData(prev => ({ ...prev, companyNo: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {mode === 'direct' ? (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Varenr. (obligatorisk)</label>
                            <input
                                type="text"
                                value={formData.itemNo || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, itemNo: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                                placeholder="Skriv varenummer..."
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Størrelsesgruppe (obligatorisk)</label>
                            <select
                                value={formData.itemBinSizeGroupID || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, itemBinSizeGroupID: parseInt(e.target.value) || undefined }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            >
                                <option value="">Velg gruppe...</option>
                                {groups.map(g => (
                                    <option key={g.itemBinSizeGroupID} value={g.itemBinSizeGroupID}>
                                        {g.itemBinSizeGroupID} - {g.description1}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Lokasjonsstørrelse (obligatorisk)</label>
                        <select
                            value={formData.binSizeNo}
                            onChange={(e) => setFormData(prev => ({ ...prev, binSizeNo: parseInt(e.target.value) || 1 }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                            disabled={loadingLookups}
                        >
                            <option value="">Velg størrelse...</option>
                            {binSizes.map(size => (
                                <option key={size.binSizeNo} value={size.binSizeNo}>
                                    {size.binSizeNo} - {size.description1}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Innlegg.prioritet</label>
                        <input
                            type="number"
                            value={formData.putAwayPriorityNo}
                            onChange={(e) => setFormData(prev => ({ ...prev, putAwayPriorityNo: parseInt(e.target.value) || 100 }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Maks antall</label>
                        <input
                            type="number"
                            value={formData.maxQuantity}
                            onChange={(e) => setFormData(prev => ({ ...prev, maxQuantity: parseInt(e.target.value) || 0 }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="submit"
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        disabled={loadingLookups}
                    >
                        Opprett
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                        Avbryt
                    </button>
                </div>
            </form>
        </div>
    );
};

export const ItemBinSizeDataTable: React.FC<ItemBinSizeDataTableProps> = ({
    selectedRows,
    onRowSelection,
    onBulkSelection,
    onDataChange,
    onOpenSidePanel,
    refreshTrigger
}) => {
    const [serverItems, setServerItems] = useState<ItemBinSizeDto[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [groups, setGroups] = useState<ItemBinSizeGroupDto[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const { user } = useAuth();
    const latestFetchParamsRef = useRef<ItemBinSizePagedQueryParams>({ pageNumber: 1, pageSize: 50 });
    const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevFiltersRef = useRef<Record<string, string>>({});

    const columnFactory = createColumnFactory<ItemBinSizeDto>();

    // Load groups for dropdown (used in create form and inline edit)
    useEffect(() => {
        const loadGroups = async () => {
            try {
                const groupsData = await itemBinSizeGroupService.getCurrent();
                setGroups(groupsData);
            } catch (err) {
                console.warn('Kunne ikke laste størrelsesgrupper:', err);
                setGroups([]);
            }
        };
        loadGroups();
    }, []);

    // Build group options for inline edit dropdown
    const groupOptions = useMemo(() => [
        { value: '', label: 'Ingen gruppe' },
        ...groups.map(g => ({
            value: String(g.itemBinSizeGroupID),
            label: `${g.itemBinSizeGroupID} - ${g.description1}`
        }))
    ], [groups]);

    // Fetch paged data
    const fetchPagedItems = useCallback(async (params: ItemBinSizePagedQueryParams) => {
        try {
            setIsLoading(true);
            setError('');
            const result = await itemBinSizeService.getPaged(params);
            setServerItems(result.items);
            setTotalRecords(result.totalRecords);
            onDataChange(result.items);
            latestFetchParamsRef.current = params;
        } catch (err: any) {
            setError(err.message || 'Feil ved lasting av data');
        } finally {
            setIsLoading(false);
        }
    }, [onDataChange]);

    // Initial load
    useEffect(() => {
        fetchPagedItems({ pageNumber: 1, pageSize: 50 });
    }, [fetchPagedItems]);

    // Refresh trigger from parent
    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            fetchPagedItems(latestFetchParamsRef.current);
        }
    }, [refreshTrigger, fetchPagedItems]);

    const refreshData = useCallback(() => {
        fetchPagedItems(latestFetchParamsRef.current);
    }, [fetchPagedItems]);

    // Server fetch handler — maps ServerFetchParams to our API query params
    const handleServerFetch = useCallback((params: ServerFetchParams) => {
        const f = params.filters || {};
        const queryParams: ItemBinSizePagedQueryParams = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            sortColumn: params.sortColumn,
            sortDirection: params.sortDirection,
            itemNo: f.itemNo || undefined,
            itemDescription: f.itemDescription || undefined,
            binSizeNo: f.binSizeDescription || undefined,
            maxQuantity: f.maxQuantity || undefined,
            putAwayPriorityNo: f.putAwayPriorityNo || undefined,
            groupDescription: f.groupDescription || undefined,
        };

        // Debounce filter changes but execute page/sort immediately
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }

        const filterKeys = ['itemNo', 'itemDescription', 'binSizeNo', 'maxQuantity', 'putAwayPriorityNo', 'groupDescription'];
        const prevFilters = prevFiltersRef.current;
        const isFilterChange = filterKeys.some(key => (f[key] || '') !== (prevFilters[key] || ''));
        prevFiltersRef.current = { ...f };

        if (isFilterChange && params.pageNumber === 1) {
            filterDebounceRef.current = setTimeout(() => {
                fetchPagedItems(queryParams);
            }, 300);
        } else {
            fetchPagedItems(queryParams);
        }
    }, [fetchPagedItems]);

    // Inline editing
    const inlineEditConfig = useInlineEdit<ItemBinSizeDto>({
        getRowId: (row) => String(row.id),
        onSave: async (_originalRow, editedData) => {
            await itemBinSizeService.update(_originalRow.id, {
                maxQuantity: editedData.maxQuantity ?? _originalRow.maxQuantity,
                putAwayPriorityNo: editedData.putAwayPriorityNo ?? _originalRow.putAwayPriorityNo,
                itemBinSizeGroupID: editedData.itemBinSizeGroupID !== undefined
                    ? (editedData.itemBinSizeGroupID || null)
                    : (_originalRow.itemBinSizeGroupID || null)
            });
        },
        onSaveSuccess: () => refreshData(),
        onSaveError: (err) => setError(err.message || 'Feil ved lagring')
    });

    // Column definitions
    const columns: ColumnDefinition<ItemBinSizeDto>[] = useMemo(() => [
        columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
            minWidth: 130,
            sortable: true,
            filterType: 'both',
            formatter: (value: any, item: ItemBinSizeDto) => {
                if (!value && item.itemBinSizeGroupID) {
                    return '(Gruppemal)';
                }
                return String(value || '');
            }
        }),
        columnFactory.createTextColumn('itemDescription', 'itemDescription', 'Varebeskrivelse', {
            minWidth: 200,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('binSizeDescription', 'binSizeDescription', 'Lokasjonsstørrelse', {
            minWidth: 160,
            sortable: true,
            filterType: 'both',
            formatter: (value: any, item: ItemBinSizeDto) =>
                value ? `${item.binSizeNo} - ${value}` : String(item.binSizeNo || '')
        }),
        columnFactory.createNumberColumn('putAwayPriorityNo', 'putAwayPriorityNo', 'Innlegg.prioritet', {
            minWidth: 130,
            sortable: true,
            filterType: 'both',
            editable: true
        }),
        columnFactory.createNumberColumn('maxQuantity', 'maxQuantity', 'Maks antall', {
            minWidth: 120,
            sortable: true,
            filterType: 'both',
            editable: true
        }),
        columnFactory.createNumberColumn('itemBinSizeGroupID', 'groupDescription', 'Størrelsegruppe', {
            minWidth: 180,
            sortable: true,
            filterType: 'both',
            formatter: (value: any, item: ItemBinSizeDto) =>
                item.itemBinSizeGroupID
                    ? `${item.itemBinSizeGroupID} - ${item.groupDescription || ''}`
                    : '',
            editable: true,
            editType: 'select',
            editOptions: groupOptions
        }),
        columnFactory.createDateColumn('created', 'created', 'Opprettet', {
            minWidth: 140,
            sortable: true
        })
    ], [columnFactory, groupOptions]);

    // Selection handling — bridge to parent's SelectedRows format
    const handleSelectionChange = useCallback((ids: (string | number)[], _selectedData: ItemBinSizeDto[]) => {
        const newIds = ids.map(id => String(id));
        setSelectedIds(newIds);

        // Sync to parent's SelectedRows format
        const newSelection: SelectedRows = {};
        newIds.forEach(id => { newSelection[id] = true; });
        onBulkSelection(newSelection);
    }, [onBulkSelection]);

    // Table actions
    const tableActions: TableAction<ItemBinSizeDto>[] = useMemo(() => [
        {
            id: 'create',
            label: 'Ny',
            icon: Plus,
            onClick: () => setShowCreateForm(prev => !prev),
            variant: 'primary' as const
        },
        {
            id: 'bulk-edit',
            label: `Rediger (${selectedIds.length})`,
            icon: Edit3,
            onClick: () => onOpenSidePanel(),
            disabled: () => selectedIds.length === 0,
            variant: 'secondary' as const
        },
        {
            id: 'refresh',
            label: 'Oppdater',
            icon: RefreshCw,
            onClick: () => refreshData(),
            variant: 'secondary' as const
        }
    ], [selectedIds.length, onOpenSidePanel, refreshData]);

    // Create handler
    const handleCreate = async (request: CreateItemBinSizeRequest) => {
        try {
            setIsLoading(true);
            await itemBinSizeService.create(request);
            setShowCreateForm(false);
            refreshData();
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            {showCreateForm && (
                <CreateForm
                    onClose={() => setShowCreateForm(false)}
                    onSave={handleCreate}
                    groups={groups}
                    companyNo={user?.companyNo || '001'}
                />
            )}

            <WarehouseTable
                data={serverItems}
                columns={columns}
                config={{
                    showPagination: true,
                    showColumnConfig: true,
                    showGlobalSearch: false,
                    showFilters: true,
                    showExport: false,
                    showPrint: false,
                    showSummary: false,
                    defaultPageSize: 50,
                    allowColumnReorder: true,
                    allowColumnResize: true,
                    stickyHeader: true,
                    compactMode: true,
                    persistenceKey: 'item-bin-size-table',
                    allowRowSelection: {
                        mode: 'multi',
                        selectAllMode: 'current-page'
                    }
                }}
                serverPagination={{
                    enabled: true,
                    totalRecords: totalRecords,
                    onFetchData: handleServerFetch
                }}
                selectedRows={selectedIds}
                onSelectionChange={handleSelectionChange}
                getRowId={(item) => String(item.id)}
                tableActions={tableActions}
                inlineEditConfig={inlineEditConfig}
                loading={isLoading}
                error={error || undefined}
            />
        </div>
    );
};
